import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faCheckCircle, faPaperPlane, faTrash, faSpinner, faImage, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import DeleteModal from "../../../common/commonmodal/DeleteModal";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const AnnouncementList = () => {
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [roleList, setRoleList] = useState([]);

    // Modals
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [noticeToDelete, setNoticeToDelete] = useState(null);
    const [isPublishOpen, setIsPublishOpen] = useState(false);
    const [noticeToPublish, setNoticeToPublish] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const employeeCode = storageService.getItem("employeeCode") || "0";
    const roleId = Number(storageService.getItem("roleId")) || 0;
    const siteId = Number(storageService.getItem("siteId")) || Number(storageService.getItem("SiteId")) || 0;
    const employeeName = storageService.getItem("employeeName") || "Admin";

    // Fetch roles list to map roleIds to names
    const fetchRoles = async () => {
        try {
            const res = await apiServices({ method: "GET", url: "/RoleMaster/GetAllRoles" });
            if (res?.success) {
                setRoleList(res.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const params = {
                userId: employeeCode,
                roleId: roleId,
                siteId: siteId,
                status: statusFilter,
                priorityId: priorityFilter,
                searchText: searchQuery,
                pageNumber: currentPage,
                pageSize: pageInfo.pageSize
            };

            const res = await apiServices({
                url: "/CommBroadcast/notice/getNotices",
                method: "GET",
                params: params
            });

            if (res.success && res.data) {
                const apiData = res.data.data || res.data.notices || res.data || [];
                const formatted = Array.isArray(apiData) ? apiData : (apiData.data || []);

                setAnnouncements(formatted);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: res.data.totalRecords || formatted.length,
                    totalPages: Math.ceil((res.data.totalRecords || formatted.length) / prev.pageSize)
                }));
            } else {
                setAnnouncements([]);
            }
        } catch (error) {
            console.error("Error fetching announcements:", error);
            toast.error("Failed to load announcements");
            setAnnouncements([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    useEffect(() => {
        fetchAnnouncements();
    }, [currentPage, pageInfo.pageSize, statusFilter, priorityFilter]);

    // Handle Search Submit/Delay
    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setCurrentPage(1);
        fetchAnnouncements();
    };

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Actions
    const handleDeleteClick = (row) => {
        setNoticeToDelete(row);
        setIsDeleteOpen(true);
    };

    const handlePublishClick = (row) => {
        setNoticeToPublish(row);
        setIsPublishOpen(true);
    };

    const confirmDelete = async () => {
        if (!noticeToDelete) return;
        setActionLoading(true);
        try {
            const payload = {
                noticeId: noticeToDelete.id || noticeToDelete.Id || noticeToDelete.noticeId,
                modifiedBy: employeeName
            };
            const res = await apiServices({
                url: "/CommBroadcast/notice/delete",
                method: "POST",
                data: payload
            });

            if (res.success) {
                toast.success("Announcement deleted successfully");
                fetchAnnouncements();
            } else {
                toast.error(res.message || "Failed to delete announcement");
            }
        } catch (error) {
            console.error("Error deleting announcement:", error);
            toast.error("An error occurred during deletion");
        } finally {
            setActionLoading(false);
            setIsDeleteOpen(false);
            setNoticeToDelete(null);
        }
    };

    const confirmPublish = async () => {
        if (!noticeToPublish) return;
        setActionLoading(true);
        try {
            const payload = {
                noticeId: noticeToPublish.id || noticeToPublish.Id || noticeToPublish.noticeId,
                modifiedBy: employeeName
            };
            const res = await apiServices({
                url: "/CommBroadcast/notice/publish",
                method: "POST",
                data: payload
            });

            if (res.success) {
                toast.success("Announcement published successfully");
                fetchAnnouncements();
            } else {
                toast.error(res.message || "Failed to publish announcement");
            }
        } catch (error) {
            console.error("Error publishing announcement:", error);
            toast.error("An error occurred during publication");
        } finally {
            setActionLoading(false);
            setIsPublishOpen(false);
            setNoticeToPublish(null);
        }
    };

    // Table Setup
    const tableHeaders = [
        { label: "NO", key: "no", align: "center" },
        { label: "TITLE", key: "title", align: "left" },
        { label: "PRIORITY", key: "priorityId", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "ACTIONS", key: "actions", align: "center" }
    ];

    const getPriorityName = (pid) => {
        const id = Number(pid);
        if (id === 4) return { name: "Critical", style: "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-100 dark:border-rose-900/50" };
        if (id === 3) return { name: "High", style: "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-100 dark:border-red-900/50" };
        if (id === 2) return { name: "Medium", style: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-900/50" };
        return { name: "Low", style: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-900/50" };
    };

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getRoleNames = (rawRoles) => {
        if (!rawRoles || !Array.isArray(rawRoles) || rawRoles.length === 0) return "All Roles";
        return rawRoles.map(item => {
            if (typeof item === 'object' && item !== null) {
                return item.roleName || item.RoleName || `Role ${item.roleId || item.RoleId}`;
            }
            const matched = roleList.find(r => Number(r.RoleId) === Number(item));
            return matched ? matched.RoleName : `Role ${item}`;
        }).join(", ");
    };

    const renderCell = (key, row, idx) => {
        switch (key) {
            case "no":
                return <span className="font-semibold text-xs text-gray-700 dark:text-textsecondary">{idx + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;

            case "title":
                return <span className="font-bold text-xs text-[#0F172A] dark:text-textprimary">{row.title || row.Title || "—"}</span>;

            case "priorityId":
                const p = getPriorityName(row.priorityId || row.PriorityId || 1);
                return (
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${p.style}`}>
                        {p.name}
                    </span>
                );

            case "status":
                const status = (row.noticeStatus || "Draft").trim();
                const isPub = status.toLowerCase() === "published" || status.toLowerCase() === "active";
                return (
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${isPub ? "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300" : "bg-gray-100 dark:bg-actionitembg text-gray-800 dark:text-textsecondary"
                        }`}>
                        {status}
                    </span>
                );

            default:
                return "—";
        }
    };

    // Expandable Additional Row - Shows details and attachments/images in one single white container box
    const expandedRowRender = (row) => {
        const attachments = row.attachments || row.Attachments || [];
        const validAttachments = attachments.filter(att => att.filePath || att.FilePath);
        let roles = row.targetRoles || row.TargetRoles || row.targetRoleIds || row.TargetRoleIds || [];
        if (roles.length === 0 && (row.targetMappings || row.TargetMappings)) {
            const mappings = row.targetMappings || row.TargetMappings || [];
            let rids = [];
            mappings.forEach(m => {
                const ids = m.roleId || m.RoleId || [];
                if (Array.isArray(ids)) {
                    rids = [...rids, ...ids];
                } else if (ids !== null && ids !== undefined) {
                    rids.push(ids);
                }
            });
            roles = rids;
        }

        return (
            <div className="p-4 bg-gray-50/50 dark:bg-actionitembg rounded-lg m-2 border border-gray-100 dark:border-bordercolor space-y-4 text-xs">
                {/* Meta details section */}
                <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-1 pb-2 border-b border-slate-100 dark:border-bordercolor">
                        <span className="font-bold text-slate-500 dark:text-textsecondary uppercase tracking-wide text-[10px]">Description</span>
                        <p className="text-slate-700 dark:text-textprimary font-medium leading-relaxed whitespace-pre-wrap">
                            {row.description || row.Description || "No description provided."}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-500 dark:text-textsecondary uppercase tracking-wide text-[10px]">Publish Date</span>
                            <span className="text-slate-800 dark:text-textprimary font-semibold">
                                {formatDate(row.publishDate || row.PublishDate)}
                            </span>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-500 dark:text-textsecondary uppercase tracking-wide text-[10px]">Expiry Date</span>
                            <span className="text-slate-800 dark:text-textprimary font-semibold">
                                {formatDate(row.expiryDate || row.ExpiryDate)}
                            </span>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="font-bold text-slate-500 dark:text-textsecondary uppercase tracking-wide text-[10px]">Targeted Roles</span>
                            <span className="text-slate-800 dark:text-textprimary font-semibold truncate" title={getRoleNames(roles)}>
                                {getRoleNames(roles)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Attachments Section - inside the same container, separated by border-t */}
                <div className="flex flex-col gap-2.5 pt-4 border-t border-slate-100 dark:border-bordercolor w-full">
                    <div className="flex items-center gap-2 text-slate-700 dark:text-textprimary font-bold text-xs uppercase tracking-wider">
                        <FontAwesomeIcon icon={faImage} className="text-slate-400" />
                        <span>Image Attachments</span>
                    </div>

                    {validAttachments.length > 0 ? (
                        <div className="flex flex-wrap gap-3">
                            {validAttachments.map((att, index) => {
                                const path = att.filePath || att.FilePath;
                                const name = att.fileName || att.FileName || `Attachment ${index + 1}`;
                                return (
                                    <div key={index} className="flex flex-col bg-white dark:bg-cardbg border border-slate-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm hover:shadow transition-shadow max-w-[200px]">
                                        <div className="w-[200px] h-[120px] bg-slate-100 dark:bg-actionitembg flex items-center justify-center overflow-hidden border-b border-slate-100 dark:border-bordercolor">
                                            <img
                                                src={path}
                                                alt={name}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                                                onClick={() => window.open(path, "_blank")}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://placehold.co/200x120?text=No+Preview";
                                                }}
                                            />
                                        </div>
                                        <div className="p-2.5 flex flex-col gap-1 w-full bg-slate-50/50 dark:bg-actionitembg">
                                            <span className="text-[10px] font-semibold truncate text-slate-700 dark:text-textprimary w-full" title={name}>
                                                {name}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-400 dark:text-textsecondary italic">No image attachments provided for this announcement.</div>
                    )}
                </div>
            </div>
        );
    };

    const isRowExpandable = (row) => {
        return true; // All rows can be expanded to view description or attachments
    };

    const getPriorityLabel = (val) => {
        if (val === "4") return "Critical";
        if (val === "3") return "High";
        if (val === "2") return "Medium";
        if (val === "1") return "Low";
        return "All Priorities";
    };

    const tableActions = (
        <div className="flex flex-col sm:flex-row items-center gap-3 text-xs w-full sm:w-auto">
            <div className="w-[140px]">
                <CustomSelect
                    value={statusFilter ? { label: statusFilter, value: statusFilter } : { label: "All Status", value: "" }}
                    options={[
                        { label: "All Status", value: "" },
                        { label: "Draft", value: "Draft" },
                        { label: "Published", value: "Published" }
                    ]}
                    onChange={(opt) => {
                        setStatusFilter(opt ? opt.value : "");
                        setCurrentPage(1);
                    }}
                    placeholder="All Status"
                />
            </div>
            <div className="w-[140px]">
                <CustomSelect
                    value={priorityFilter ? { label: getPriorityLabel(priorityFilter), value: priorityFilter } : { label: "All Priorities", value: "" }}
                    options={[
                        { label: "All Priorities", value: "" },
                        { label: "Low", value: "1" },
                        { label: "Medium", value: "2" },
                        { label: "High", value: "3" },
                        { label: "Critical", value: "4" }
                    ]}
                    onChange={(opt) => {
                        setPriorityFilter(opt ? opt.value : "");
                        setCurrentPage(1);
                    }}
                    placeholder="All Priorities"
                />
            </div>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading="Announcements"
                subHeading="Manage and broadcast updates or notices to users and tenants."
                primaryBtnText="Create Announcement"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("/zentcomm/announcement/add")}
            />

            {/* Announcements Table */}
            <div className="flex-1 w-full mt-6">
                <CommonTable
                    headers={tableHeaders}
                    data={announcements.map((row, idx) => ({
                        ...row,
                        pageIndex: idx,
                        uniqueKey: `announcement-${row.id || row.Id || row.noticeId || idx}`
                    }))}
                    renderCell={(key, row) => renderCell(key, row, row.pageIndex)}
                    loading={loading}
                    tableHeading="Announcement Logs"
                    headerAction={tableActions}
                    pagination={{
                        pageNumber: currentPage,
                        pageSize: pageInfo.pageSize,
                        totalPages: pageInfo.totalPages,
                        totalRecords: pageInfo.totalRecords
                    }}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    onDelete={handleDeleteClick}
                    onEdit={(row) => navigate(`/zentcomm/announcement/edit/${row.id || row.Id || row.noticeId}`, { state: { item: row } })}
                    expandedRowRender={expandedRowRender}
                    isRowExpandable={isRowExpandable}
                    customAction={{
                        label: "Publish",
                        icon: faPaperPlane,
                        hide: (row) => {
                            const status = (row.status || row.Status || "Draft").trim().toLowerCase();
                            return status === "published" || status === "active";
                        },
                        onClick: handlePublishClick
                    }}
                />
            </div>

            {/* Delete Modal */}
            {isDeleteOpen && (
                <DeleteModal
                    isOpen={isDeleteOpen}
                    onClose={() => setIsDeleteOpen(false)}
                    onConfirm={confirmDelete}
                    title="Delete Announcement"
                    message={`Are you sure you want to delete "${noticeToDelete?.title || "this announcement"}"? This action cannot be undone.`}
                    loading={actionLoading}
                />
            )}

            {/* Custom Publish Modal */}
            {isPublishOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-lg" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-textprimary">Publish Announcement</h3>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-textsecondary leading-relaxed">
                                Are you sure you want to publish "{noticeToPublish?.title || noticeToPublish?.Title || "this announcement"}"? Once published, it will be visible to all users with matching target roles.
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 dark:bg-actionitembg border-t border-gray-100 dark:border-bordercolor flex justify-end gap-3">
                            <button
                                disabled={actionLoading}
                                onClick={() => setIsPublishOpen(false)}
                                className="px-4 py-2 border border-gray-200 dark:border-bordercolor text-xs font-bold text-gray-500 dark:text-textsecondary rounded-xl bg-transparent hover:bg-gray-100 dark:hover:bg-actionitembg transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={actionLoading}
                                onClick={confirmPublish}
                                className="px-5 py-2 text-xs font-bold text-white rounded-xl bg-emerald-600 hover:bg-emerald-700 transition-all flex items-center gap-2"
                            >
                                {actionLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : "Publish Now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementList;
